import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/site";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATES = ["QLD", "NSW", "VIC", "WA", "SA", "TAS", "NT", "ACT"];
const EMPTY = {
  business_name: "",
  contact_name: "",
  email: "",
  phone: "",
  state: "",
  location: "",
  website: "",
  services: "",
  message: "",
};

/**
 * GetListedDialog — capture form/modal so genuine providers can request
 * inclusion in the directory. Posts to /api/provider-listings.
 */
export default function GetListedDialog({
  triggerLabel = "Get listed",
  triggerClassName = "btn-primary",
  triggerTestId = "get-listed-trigger",
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (
      !form.business_name.trim() ||
      !form.contact_name.trim() ||
      !form.email.trim() ||
      !form.state
    ) {
      toast.error("Please add your business name, contact name, email and state.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/provider-listings", form);
      toast.success("Thank you. Your request has been received and we will be in touch.");
      setForm(EMPTY);
      setOpen(false);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" className={triggerClassName} data-testid={triggerTestId}>
          {triggerLabel}
        </button>
      </DialogTrigger>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="get-listed-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-forest-900 tracking-tight">
            List your water cremation service
          </DialogTitle>
          <DialogDescription className="text-forest-700 leading-relaxed">
            If you operate a genuine pet water cremation service in Australia, share
            a few details and we will consider you for this educational directory.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="get-listed-form">
          <div className="space-y-1.5">
            <Label htmlFor="gl-business">Business name *</Label>
            <Input
              id="gl-business"
              value={form.business_name}
              onChange={set("business_name")}
              data-testid="listing-business-name"
              placeholder="Your business name"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="gl-contact">Contact name *</Label>
              <Input
                id="gl-contact"
                value={form.contact_name}
                onChange={set("contact_name")}
                data-testid="listing-contact-name"
                placeholder="Your name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="gl-email">Email *</Label>
              <Input
                id="gl-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                data-testid="listing-email"
                placeholder="you@business.com.au"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="gl-phone">Phone</Label>
              <Input
                id="gl-phone"
                value={form.phone}
                onChange={set("phone")}
                data-testid="listing-phone"
                placeholder="Optional"
              />
            </div>
            <div className="space-y-1.5">
              <Label>State *</Label>
              <Select value={form.state} onValueChange={set("state")}>
                <SelectTrigger data-testid="listing-state">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {STATES.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`listing-state-${s}`}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="gl-location">Suburb / town</Label>
              <Input
                id="gl-location"
                value={form.location}
                onChange={set("location")}
                data-testid="listing-location"
                placeholder="Optional"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="gl-website">Website</Label>
              <Input
                id="gl-website"
                value={form.website}
                onChange={set("website")}
                data-testid="listing-website"
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="gl-services">Services offered</Label>
            <Input
              id="gl-services"
              value={form.services}
              onChange={set("services")}
              data-testid="listing-services"
              placeholder="e.g. Water cremation, home collection, memorial urns"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="gl-message">Anything else?</Label>
            <Textarea
              id="gl-message"
              value={form.message}
              onChange={set("message")}
              data-testid="listing-message"
              placeholder="Optional"
              rows={3}
            />
          </div>

          <button
            type="submit"
            className="btn-primary w-full justify-center"
            disabled={submitting}
            data-testid="listing-submit"
          >
            {submitting && <Loader2 size={16} className="animate-spin" />}
            {submitting ? "Sending" : "Submit request"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
